import { ArrowRight } from 'lucide-react'

import { useStageSetsOfKind } from '@/features/projects/api'
import { useUsers } from '@/shared/hooks/use-users'
import { useI18n, useT, type I18n } from '@/shared/i18n'
import { formatDateMedium } from '@/shared/i18n'
import type { ActivityChange, UserSummary } from '@/shared/types/domain'

import { TEXTO_LARGO } from '../activity-diff'

/**
 * Nombres de campo en CLAVES del catálogo.
 *
 * Un campo sin entrada acá se muestra con su nombre crudo (`client_contact_id`)
 * en vez de esconderse: que se vea feo es mejor que no contar que algo cambió.
 * El que moleste se agrega a la tabla.
 */
const CAMPO_KEY: Record<string, string> = {
  name: 'common.label.name',
  stage_id: 'common.label.phase',
  internal_owner_id: 'common.label.owner',
  assignee_id: 'common.label.owner',
  health: 'projects.field.health',
  start_date: 'projects.field.startDate',
  target_end_date: 'projects.field.targetEndDate',
  actual_end_date: 'projects.field.actualEndDate',
  due_date: 'tasks.field.dueDate',
  is_client_visible: 'projects.field.clientVisible',
  is_ongoing: 'projects.field.ongoing',
  title: 'common.label.title',
  visibility: 'docs.form.type',
  status: 'common.label.status',
  completed_at: 'projects.field.completedAt',
}

export interface ActivityDiffProps {
  changes: ActivityChange[]
}

/**
 * "De qué a qué", que es la mitad de la pregunta que faltaba.
 *
 * Los valores llegan crudos de `audit_log` —ids, booleanos, fechas ISO— y acá
 * se vuelven legibles. Un id sin resolver es exactamente igual de inútil que no
 * mostrar nada, así que las fases y las personas se buscan por id y sólo se
 * cae al id crudo cuando no se encuentran.
 */
export function ActivityDiff({ changes }: ActivityDiffProps) {
  const t = useT()
  const { locale } = useI18n()
  /*
    Los sets CRUDOS, no las fases aplanadas.

    `useProjectStages()` de-duplica por `key` y se queda con las del SISTEMA,
    pero cada proyecto vive en el set clonado de su empresa: los ids no
    coinciden y ninguna fase resolvería. Es exactamente el mismo error que
    dejó el filtro de fase del portafolio sin devolver nada nunca, así que acá
    se buscan sobre todos los sets.
  */
  const { data: stageSets = [] } = useStageSetsOfKind('project')
  // Los sets crudos traen `StageRead` (con `label`), no el `Stage` del
  // dominio (con `name`). Se aplana a lo mínimo que hace falta acá.
  const stages: NombrePorId[] = stageSets.flatMap((set) =>
    set.stages.map((stage) => ({ id: stage.id, name: stage.label })),
  )
  const { data: users = [] } = useUsers()

  if (changes.length === 0) return null

  return (
    <ul className="flex flex-col gap-0.5 pl-6">
      {changes.map((cambio) => (
        <li key={cambio.field} className="flex flex-wrap items-center gap-1.5 text-2xs">
          <span className="text-text-3">{nombreDeCampo(t, cambio.field)}</span>

          {TEXTO_LARGO.has(cambio.field) ? (
            // El contenido NO se vuelca: un brief son párrafos y convertiría la
            // lista de actividad en un documento. Se anuncia y se abre aparte.
            <span className="text-text-2">{t('projects.activity.textChanged')}</span>
          ) : (
            <>
              <Valor bruto={cambio.before} stages={stages} users={users} locale={locale} t={t} />
              <ArrowRight size={10} aria-hidden="true" className="text-text-3" />
              <Valor
                bruto={cambio.after}
                stages={stages}
                users={users}
                locale={locale}
                t={t}
                destacado
              />
            </>
          )}
        </li>
      ))}
    </ul>
  )
}

function nombreDeCampo(t: I18n['t'], field: string): string {
  const clave = CAMPO_KEY[field]
  return clave ? t(clave) : field
}

/** Lo único que este archivo necesita de una fase o de una persona. */
interface NombrePorId {
  id: string
  name: string
}

interface ValorProps {
  bruto: unknown
  stages: readonly NombrePorId[]
  users: readonly UserSummary[]
  locale: Parameters<typeof formatDateMedium>[1]
  t: I18n['t']
  /** El valor nuevo va con más contraste: es el que se lee primero. */
  destacado?: boolean
}

function Valor({ bruto, stages, users, locale, t, destacado }: ValorProps) {
  const texto = legible(bruto, stages, users, locale, t)
  const vacio = bruto === null || bruto === undefined || bruto === ''

  return (
    <span
      className={
        vacio
          ? 'text-text-3 italic'
          : destacado
            ? 'hairline rounded-[var(--radius-control)] px-1 text-text-1'
            : 'text-text-2'
      }
    >
      {texto}
    </span>
  )
}

/**
 * Un valor de `audit_log`, en palabras.
 *
 * El orden importa: primero lo que puede resolverse contra otra tabla (fases,
 * personas), después las formas genéricas. Al revés, un uuid caería en "texto"
 * y se mostraría crudo.
 */
function legible(
  bruto: unknown,
  stages: readonly NombrePorId[],
  users: readonly UserSummary[],
  locale: Parameters<typeof formatDateMedium>[1],
  t: I18n['t'],
): string {
  if (bruto === null || bruto === undefined || bruto === '')
    return t('projects.activity.emptyValue')
  if (typeof bruto === 'boolean') return bruto ? t('common.state.yes') : t('common.state.no')

  const texto = String(bruto)

  const fase = stages.find((stage) => stage.id === texto)
  if (fase) return fase.name

  const persona = users.find((usuario) => usuario.id === texto)
  if (persona) return persona.display_name

  // `2026-09-20` o `2026-09-20T13:00:00Z`: se muestra el día.
  if (/^\d{4}-\d{2}-\d{2}/.test(texto)) return formatDateMedium(texto.slice(0, 10), locale)

  // Un uuid que no resolvió contra nada: mostrarlo entero es ruido; los ocho
  // primeros alcanzan para reconocerlo si alguien lo va a buscar.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(texto)) return `${texto.slice(0, 8)}…`

  return texto.length > 60 ? `${texto.slice(0, 60)}…` : texto
}
